import { i18n } from '@lingui/core';
import { msg, plural, t } from '@lingui/core/macro';
import { z } from 'zod';

import { filterAction } from '@/shared/filters/filter-action';
import { listPageAction } from '@/shared/patterns/list-search';
import { resolvePatternText } from '@/shared/patterns/text';
import { action, can, defineScreen } from '@/shared/screen/define';
import type { ScreenContext } from '@/shared/screen/types';

import { billingSettingFilters } from './billing-setting.filters';

// Manifesto da Régua de cobrança (U23/U42). Arquivo leve: entra no `src/app/screens.ts` e no ⌘K; o
// fio da ação com efeito vem por `import()` na hora de rodar. As ações não têm gate próprio no
// legado (BillingSettingHeader.tsx:29-47, BillingSettingList.tsx:123-131): quem decide é a API.

export const BILLING_SETTING_PATH = '/gestao-carteira/parametros';

const loadApi = () => import('./billing-setting.api');

/** Modo criar, o mesmo `NEW_BILLING_SETTING` de `shared/form` (importá-lo puxaria o formulário para o JS inicial). */
export const NEW_BILLING_SETTING = 'novo';
const idInput = z.object({ id: z.string().min(1).max(64) });

/** "Atualizar" (BillingSettingHeader.tsx:29-38). A tela liga o `run` ao refetch da lista. */
export const billingSettingRefresh = action({
  id: 'billingSetting.refresh',
  kind: 'read',
  label: msg`Atualizar`,
  description: msg`Busca de novo a lista de réguas de cobrança, sem mudar nada.`,
  run: () => undefined,
});

/** "Nova régua de cobrança" abre o sheet vazio (BillingSettingHeader.tsx:39-47). */
export const billingSettingCreate = action({
  id: 'billingSetting.create',
  kind: 'draft',
  label: msg`Nova régua de cobrança`,
  description: msg`Abre o formulário em branco de régua de cobrança: juros, multa, protesto e custos de liquidação antecipada de um convênio. Nada é gravado até a pessoa salvar.`,
  keywords: [msg`régua`, msg`juros`, msg`multa`, msg`cadastrar`],
  shortcut: 'n',
  run: ({ nav }) => nav.search({ edit: NEW_BILLING_SETTING }),
});

/** Lápis e clique na linha (BillingSettingList.tsx:123,157): abre o sheet com a linha. */
export const billingSettingEdit = action({
  id: 'billingSetting.edit',
  kind: 'draft',
  row: true,
  label: msg`Editar`,
  description: msg`Abre o formulário de edição de uma régua de cobrança da lista, pelo id. Nada é gravado até a pessoa salvar.`,
  input: idInput,
  run: ({ nav, input }) => nav.search({ edit: input.id }),
});

/** Lixeira (BillingSettingList.tsx:126-131, modal em Drawer…Container.tsx:123-136). */
export const billingSettingDelete = action({
  id: 'billingSetting.delete',
  kind: 'destructive',
  page: true,
  row: true,
  label: msg`Excluir`,
  description: msg`Exclui de vez uma régua de cobrança, pelo id. Não dá para desfazer. Pede a confirmação da pessoa.`,
  input: idInput.extend({ name: z.string().max(200).optional() }),
  // U20-6: o verbo e o nome; o texto é o do legado ("Tem certeza que deseja excluir esse modelo?").
  preview: (input) => {
    const name = input.name ?? t`esta régua de cobrança`;
    return { title: t`Excluir ${name}?`, detail: t`Tem certeza que deseja excluir esta régua de cobrança?`, tone: 'destructive' };
  },
  run: async ({ input }) => {
    const api = await loadApi();
    await api.deleteBillingSetting(input.id);
    return input.id;
  },
});

export const billingSettingFilter = filterAction('billingSetting.filter', billingSettingFilters, {
  description: msg`Filtra a lista de réguas de cobrança: name (o nome contém) e walletsCode (um ou mais códigos de convênio, só dígitos). Filtro omitido é limpo.`,
});

export const billingSettingListPage = listPageAction('billingSetting.list.page');

export const billingSettingListScreen = defineScreen({
  id: 'billingSetting.list',
  path: BILLING_SETTING_PATH,
  title: msg`Régua de cobrança`,
  description: msg`Réguas de cobrança por convênio (juros, multa, instrução de protesto, baixa automática e custos de liquidação antecipada): lista, filtro por nome ou código do convênio, criar, editar e excluir.`,
  permission: can('BillingAccount', 'Read'),
  place: { icon: 'wallet', section: 'wallet-management' },
  actions: [billingSettingRefresh, billingSettingCreate, billingSettingFilter, billingSettingListPage, billingSettingEdit, billingSettingDelete],
});

/** O que o agente lê da tela (U24, U46): contagem, filtros pelo rótulo e a régua aberta, no idioma da tela. */
export function billingSettingContext(input: { readonly total: number | undefined; readonly filters: readonly string[]; readonly editing: string | undefined; readonly editingName?: string | undefined }): ScreenContext {
  const total = input.total;
  const count = total === undefined ? t`Carregando.` : plural(total, { one: '# régua na lista.', other: '# réguas na lista.' });
  const labels = input.filters.map((key) => {
    const label = billingSettingFilters.fieldOf(key)?.label;
    return label ? resolvePatternText(i18n, label) : key;
  });
  const filterList = labels.join(', ');
  const filters = labels.length > 0 ? t`Filtros: ${filterList}.` : t`Sem filtros.`;
  const name = input.editingName ?? input.editing ?? '';
  const editing =
    input.editing === undefined ? '' : input.editing === NEW_BILLING_SETTING ? t`Formulário de nova régua aberto.` : t`Formulário da régua ${name} aberto.`;
  return {
    summary: [t`Lista de réguas de cobrança.`, count, filters, editing].filter(Boolean).join(' '),
    fields: { total: input.total ?? null },
    sensitivity: 'internal',
  };
}
